import * as React from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import moment from "moment";
import { getDashboard } from "../../app/store/actions/whFinanceActions";
import DateRangePicker from "../../app/components/DateRangePicker";
import { isExternalFinanceStoreSubscription } from "../../financeTierAccess";
import OsDashboardWidgets from "../WHFinance/OsDashboardWidgets";

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-EG", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value || 0);

const Stat = ({ label, value, note, rule }) => (
  <div className={`fo-stat${rule ? " rule" : ""}`}>
    <div className="fo-card-title">{label}</div>
    <div className="fo-stat-value">
      <span className="fo-stat-amount">{formatCurrency(value)}</span>
      <span>EGP</span>
    </div>
    {note && <p>{note}</p>}
  </div>
);

const Kpi = ({ label, value, note, hot }) => (
  <div className={`fo-kpi${hot ? " hot" : ""}`}>
    <div className="fo-kpi-label">
      <span className="fo-dot" />
      {label}
    </div>
    <strong>{typeof value === "number" ? formatCurrency(value) : value}</strong>
    {note && <em>{note}</em>}
  </div>
);

const Meter = ({ label, value, share, width }) => (
  <div>
    <div className="fo-meter">
      <span style={{ width: "auto", textAlign: "left", color: "var(--tx)", fontSize: "13.5px" }}>{label}</span>
      <b>{formatCurrency(value)} EGP</b>
      <span>{share}</span>
    </div>
    <div className="fo-track">
      <i style={{ width: `${width || 0}%` }} />
    </div>
  </div>
);

const DashboardSkeleton = () => (
  <div className="fo-stack">
    <div className="fo-skel" style={{ height: 220 }} />
    <div className="fo-skel" style={{ height: 120 }} />
    <div className="fo-grid-2">
      <div className="fo-skel" style={{ height: 280 }} />
      <div className="fo-skel" style={{ height: 280 }} />
    </div>
  </div>
);

export const WHFinanceHome = ({ getDashboard, dashboard, isLoading, user }) => {
  const navigate = useNavigate();
  const external = isExternalFinanceStoreSubscription(user);
  const [startDate, setStartDate] = useState(moment().startOf("month"));
  const [endDate, setEndDate] = useState(moment().endOf("month"));

  const formatDateParam = (date) => date?.format?.("YYYY-MM-DD") ?? date;

  useEffect(() => {
    getDashboard({
      startDate: formatDateParam(startDate),
      endDate: formatDateParam(endDate),
    });
  }, []);

  const handleFilter = () => {
    getDashboard({
      startDate: formatDateParam(startDate),
      endDate: formatDateParam(endDate),
    });
  };

  const shippingExpenses = dashboard?.totalShippingFees || 0;
  const shippingSurcharges = dashboard?.totalShippingSurchages || 0;
  const totalCodCollected = dashboard?.totalCashCollected || 0;
  const payouts = dashboard?.totalPayouts || 0;
  const compensation = dashboard?.totalCompensation || 0;
  const backtrack = dashboard?.totalBacktrackFees || 0;
  const chainzIncome = dashboard?.chainzIncome ?? dashboard?.totalFees ?? 0;
  const externalNetIncome = totalCodCollected - shippingExpenses;
  const netBalance = totalCodCollected - payouts;
  const period = `${startDate?.format?.("MMM D, YYYY") || ""} – ${endDate?.format?.("MMM D, YYYY") || ""}`;

  const feeBreakdown = [
    { key: "totalHandlingFees", label: "Handling fees", value: dashboard?.totalHandlingFees || 0 },
    { key: "totalSubscriptionFees", label: "Subscription fees", value: dashboard?.totalSubscriptionFees || 0 },
    { key: "totalBacktrackFees", label: "Backtrack fees", value: backtrack },
    { key: "totalInboundFees", label: "Inbound fees", value: dashboard?.totalInboundFees || 0 },
    { key: "totalOutboundFees", label: "Outbound fees", value: dashboard?.totalOutboundFees || 0 },
    { key: "totalShippingSurchages", label: "Shipping surcharges", value: shippingSurcharges },
    { key: "totalOtherFees", label: "Other fees", value: dashboard?.totalOtherFees || 0 },
  ]
    .filter((item) => item.value)
    .sort((a, b) => b.value - a.value);

  const maxFee = feeBreakdown.length ? Math.max(...feeBreakdown.map((item) => item.value)) : 1;

  const funnel = [
    { label: "COD collected", value: totalCodCollected, note: "Cash collected from customers" },
    { label: "Chainz income", value: chainzIncome, note: "Service fees and shipping surcharges" },
    { label: "Shipping expenses", value: shippingExpenses, note: "Paid to shipping companies", neg: true },
    { label: "Vendor payouts", value: payouts, note: "Paid out to vendors", neg: true },
  ];

  const topVendors = (dashboard?.codPerVendor || [])
    .slice()
    .sort((a, b) => b.totalCod - a.totalCod)
    .slice(0, 5)
    .map((vendor) => ({
      name: vendor.vendor,
      cod: vendor.totalCod,
      fees: (dashboard?.feesPerVendor || []).find((fee) => fee.vendor === vendor.vendor)?.totalFees || 0,
    }));

  return (
    <div className="fo-stack">
      <div className="fo-card fo-filter">
        <div className="fo-grow">
          <div className="fo-card-title" style={{ marginBottom: 8 }}>Date range</div>
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onChange={({ startDate: nextStart, endDate: nextEnd }) => {
              if (nextStart) setStartDate(nextStart);
              if (nextEnd) setEndDate(nextEnd);
            }}
            fullWidth
            margin="dense"
            placeholder="Select date range"
          />
        </div>
        <button type="button" className="fo-btn fo-btn-primary" onClick={handleFilter} disabled={isLoading}>
          Apply
        </button>
      </div>

      {isLoading ? (
        <DashboardSkeleton />
      ) : external ? (
        <>
          <section className="fo-hero">
            <div>
              <div className="fo-card-title" style={{ color: "var(--red-tx3)" }}>COD collected · {period}</div>
              <div className="fo-hero-value">
                {formatCurrency(totalCodCollected)}
                <span>EGP</span>
              </div>
              <div className="fo-hero-sub">External subscription · carrier settlement view</div>
            </div>
            <div className="fo-hero-side">
              <Stat label="Shipping expenses" value={shippingExpenses} note="Paid to shipping companies" rule />
              <Stat label="Net income" value={externalNetIncome} note="COD collected minus shipping expenses" rule />
            </div>
          </section>
          <section className="fo-band">
            <Kpi label="COD collected" value={totalCodCollected} note={period} />
            <Kpi label="Shipping expenses" value={shippingExpenses} note="Carrier cost" hot />
            <Kpi label="Net income" value={externalNetIncome} note="After shipping" />
          </section>
        </>
      ) : (
        <>
          <section className="fo-hero">
            <div>
              <div className="fo-card-title" style={{ color: "var(--red-tx3)" }}>Chainz income · {period}</div>
              <div className="fo-hero-value">
                {formatCurrency(chainzIncome)}
                <span>EGP</span>
              </div>
              <div className="fo-hero-sub">Service fees and shipping surcharges, excluding carrier shipping expenses</div>
            </div>
            <div className="fo-hero-side">
              <Stat label="COD collected" value={totalCodCollected} rule />
              <Stat label="Shipping expenses" value={shippingExpenses} note="Paid to shipping companies" rule />
              <Stat label="Vendor payouts" value={payouts} note="Paid to vendors" />
              <Stat label="Net balance" value={netBalance} note="COD collected minus payouts" />
            </div>
          </section>

          <section className="fo-band">
            <Kpi label="COD collected" value={totalCodCollected} note={period} />
            <Kpi label="Backtrack fees" value={backtrack} note="Backtrack fee revenue" />
            <Kpi label="Compensation" value={compensation} note="Issued in this period" />
            <Kpi label="Shipping surcharges" value={shippingSurcharges} note="Retained by Chainz" hot={shippingSurcharges > 0} />
          </section>

          <div className="fo-split">
            <section className="fo-card" style={{ display: "flex", flexDirection: "column" }}>
              <div className="fo-card-head">
                <div>
                  <div className="fo-card-title">Revenue mix</div>
                  <h2>Revenue by service</h2>
                </div>
              </div>
              {feeBreakdown.length === 0 && <p style={{ color: "var(--mut)", margin: 0 }}>No fees in this period.</p>}
              {feeBreakdown.map((item) => {
                const share = chainzIncome ? (item.value / chainzIncome) * 100 : 0;
                const width = maxFee ? (item.value / maxFee) * 100 : 0;
                return (
                  <Meter
                    key={item.key}
                    label={item.label}
                    value={item.value}
                    share={`${share.toFixed(1)}%`}
                    width={width}
                  />
                );
              })}
              <div className="fo-total-rev">
                <div className="fo-card-title">Total revenue</div>
                <div className="fo-stat-value" style={{ fontSize: 36 }}>
                  {formatCurrency(chainzIncome)}
                  <span>EGP</span>
                </div>
                <p>Service fees and shipping surcharges for this period</p>
              </div>
            </section>

            <section className="fo-card" style={{ display: "flex", flexDirection: "column" }}>
              <div className="fo-card-head">
                <div>
                  <div className="fo-card-title">Cashflow · {period}</div>
                  <h2>Where the cash goes</h2>
                </div>
              </div>
              <div>
                {funnel.map((item) => (
                  <div className="fo-line" key={item.label}>
                    <span>{item.label}</span>
                    <b className={item.neg ? "neg" : ""}>{formatCurrency(item.value)} EGP</b>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: "auto", paddingTop: 22 }}>
                <div className="fo-card-title">Net balance</div>
                <div className="fo-stat-value" style={{ fontSize: 36 }}>
                  {formatCurrency(netBalance)}
                  <span>EGP</span>
                </div>
                <p style={{ color: "var(--mut2)", fontSize: 12, marginTop: 6 }}>COD collected minus vendor payouts</p>
              </div>
            </section>
          </div>

          <section className="fo-card">
            <div className="fo-card-head">
              <div>
                <div className="fo-card-title">{period}</div>
                <h2>Top brands by COD</h2>
              </div>
              <button type="button" className="fo-link" onClick={() => navigate("/warehousing/finance/vendors")}>
                View vendors
              </button>
            </div>
            {topVendors.length === 0 && <p style={{ color: "var(--mut)", margin: 0 }}>No vendor data in this period.</p>}
            {topVendors.map((vendor, index) => (
              <div className="fo-line" key={vendor.name}>
                <span className="mono">{String(index + 1).padStart(2, "0")}</span>
                <span style={{ color: "var(--tx)", fontWeight: 500 }}>{vendor.name}</span>
                <b>{formatCurrency(vendor.cod)} EGP</b>
              </div>
            ))}
          </section>
          <OsDashboardWidgets startDate={formatDateParam(startDate)} endDate={formatDateParam(endDate)} />
        </>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  dashboard: state?.records?.dashboard,
  isLoading: state?.records?.isLoading || state?.wait?.isLoading,
  user: state?.auth?.user,
});

export default connect(mapStateToProps, { getDashboard })(WHFinanceHome);
